import { useState } from 'react'
import { Building2, Check, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCreateOrg, useMe, useSwitchOrg } from '../api/auth'
import { formatCurrencyUSD } from '../i18n/formatters'

export function OrganizationsPage() {
  const { t } = useTranslation('auth')
  const { data: me, isLoading } = useMe()
  const switchOrg = useSwitchOrg()
  const createOrg = useCreateOrg()
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (isLoading || !me) {
    return <div className="p-8 text-text-secondary">{t('common.loading')}</div>
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await createOrg.mutateAsync({ name: name.trim(), slug: slug.trim().toLowerCase() })
      setShowCreate(false)
      setName('')
      setSlug('')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('organizations.title')}</h1>
          <p className="text-sm text-text-secondary">
            {t('organizations.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> {t('organizations.newOrg')}
        </button>
      </header>

      <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
        {me.memberships.map((m) => {
          const isActive = m.org_id === me.active_org_id
          const org = m.org
          return (
            <li key={m.org_id} className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-text-secondary" />
                <div>
                  <div className="font-medium">{org?.name ?? m.org_id}</div>
                  <div className="text-xs text-text-secondary">
                    {org?.slug ?? m.org_id} · {m.role}
                    {org && ` · ${t('organizations.dailyCap', { value: formatCurrencyUSD(org.spend_cap_daily_usd, 2) })}`}
                  </div>
                </div>
              </div>
              {isActive ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
                  <Check className="h-3 w-3" /> {t('organizations.active')}
                </span>
              ) : (
                <button
                  onClick={() => switchOrg.mutate(m.org_id)}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-surface-elevated"
                >
                  {t('organizations.switch')}
                </button>
              )}
            </li>
          )
        })}
        {me.memberships.length === 0 && (
          <li className="p-6 text-center text-sm text-text-secondary">
            {t('organizations.noMemberships')}
          </li>
        )}
      </ul>

      {showCreate && (
        <form onSubmit={onCreate} className="space-y-3 rounded-lg border border-border bg-surface p-4">
          <h2 className="font-medium">{t('organizations.createTitle')}</h2>
          <div>
            <label className="block text-xs font-medium text-text-secondary">{t('organizations.nameLabel')}</label>
            <input
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('organizations.namePlaceholder')}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t('organizations.slugLabel')}
            </label>
            <input
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={t('organizations.slugPlaceholder')}
              pattern="^[a-z0-9-]+$"
              required
            />
          </div>
          {error && <div className="text-sm text-error">{error}</div>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createOrg.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {createOrg.isPending ? t('common:actions.creating') : t('common:actions.create')}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-surface-elevated"
            >
              {t('common:actions.cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
